/**
 * Home page feed data via WordPress REST API (replaces GraphQL for page.js).
 * Each function returns data in the same shape as the former GraphQL response
 * so existing components (EmblaCarousel, LatestChallengeParticipants) work unchanged.
 */
import { fetchWithTimeout } from '@hasangad/figmawya-shared';

const baseURL = process.env.NEXT_PUBLIC_BASE_URL || '';

/** Map WP REST _embed item to GraphQL-like shape for carousel */
function mapWpItemToFeedNode(item) {
  const featured = item?._embedded?.['wp:featuredmedia']?.[0];
  const author = item?._embedded?.author?.[0];
  return {
    id: item.id,
    title: item.title?.rendered ?? item.title ?? '',
    date: item.date,
    author: author ? { node: { name: author.name } } : null,
    featuredImage: featured ? { node: { sourceUrl: featured.source_url } } : null,
  };
}

/** Normalize ACF link type for EmblaCarousel short_ad (expects linkType[0]). */
function normalizeShortAdLinkType(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  return [raw];
}

/**
 * Short ads carousel: WP REST short_ad → same shape as former GraphQL shortsAds.nodes.
 */
function mapShortAdToCarouselNode(item) {
  const embedded = item?._embedded?.['wp:featuredmedia']?.[0];
  const fromEmbed = embedded?.source_url;
  const fromTheme = item?.featured_image_urls?.medium_large;
  const acf = item?.acf && typeof item.acf === 'object' ? item.acf : {};
  const shortAdLink =
    acf.shortAdLink ??
    acf.short_ad_link ??
    acf.short_ad_url ??
    '';
  const linkTypeRaw =
    acf.linkType ??
    acf.link_type ??
    acf.acf_shorts?.linkType ??
    acf.acf_shorts?.link_type;
  return {
    id: item.id,
    title: item.title?.rendered ?? item.title ?? '',
    date: item.date,
    featuredImage:
      fromEmbed || fromTheme
        ? { node: { sourceUrl: fromEmbed || fromTheme } }
        : null,
    acfShorts: {
      shortAdLink: typeof shortAdLink === 'string' ? shortAdLink : String(shortAdLink || ''),
      linkType: normalizeShortAdLinkType(linkTypeRaw),
    },
  };
}

/**
 * Short ads (short_ad CPT) for home top carousel.
 */
export async function getShortAdsForHome() {
  try {
    const res = await fetchWithTimeout(
      `${baseURL}api/wp/v2/short_ad?per_page=12&_embed&orderby=date&order=desc`
    );
    if (!res.ok) return [];
    const data = await res.json();
    const list = Array.isArray(data) ? data : [];
    return list.map(mapShortAdToCarouselNode);
  } catch (err) {
    console.error('getShortAdsForHome error:', err);
    return [];
  }
}

/**
 * Challenges for home (latest one with participants).
 * Returns array of { challengeId, title, date, acf, participants, participantCount }.
 */
export async function getChallengesForHome() {
  try {
    const listRes = await fetchWithTimeout(
      `${baseURL}api/wp/v2/challenge?per_page=1&_embed`
    );
    if (!listRes.ok) return [];
    const list = await listRes.json();
    const challenge = Array.isArray(list) ? list[0] : null;
    if (!challenge) return [];

    const challengeId = challenge.id;
    let participants = [];
    let participantCount = 0;
    try {
      const partRes = await fetchWithTimeout(
        `${baseURL}api/challenges/v1/participants/${challengeId}`
      );
      if (partRes.ok) {
        const data = await partRes.json();
        participants = Array.isArray(data?.participants) ? data.participants : [];
        participantCount = participants.length;
      }
    } catch (_) {}

    const node = {
      challengeId,
      title: challenge.title?.rendered ?? challenge.title ?? '',
      date: challenge.date,
      acf: challenge.acf || { deadline: null, difficulty: null },
      participants: participants.map((p) => ({
        id: p.id,
        displayName: p.displayName ?? p.display_name ?? p.name ?? '',
        name: p.displayName ?? p.display_name ?? p.name ?? '',
        slug: p.slug ?? p.nicename ?? p.user_nicename ?? '',
        acceptedAt: p.acceptedAt ?? p.accepted_at,
        status: p.status,
        avatar: p.avatar ?? p.avatar_url,
      })),
      participantCount,
    };
    return [node];
  } catch (err) {
    console.error('getChallengesForHome error:', err);
    return [];
  }
}

/**
 * Posts (articles) for home carousel. Returns nodes in GraphQL-like shape.
 */
export async function getPostsForHome() {
  try {
    const res = await fetchWithTimeout(
      `${baseURL}api/wp/v2/posts?per_page=6&_embed`
    );
    if (!res.ok) return [];
    const data = await res.json();
    const list = Array.isArray(data) ? data : [];
    return list.map(mapWpItemToFeedNode);
  } catch (err) {
    console.error('getPostsForHome error:', err);
    return [];
  }
}

/**
 * Sessions for home carousel. Uses sessions v2 list; maps to GraphQL-like shape.
 */
export async function getSessionsForHome() {
  try {
    const res = await fetchWithTimeout(`${baseURL}api/sessions/v2/list?_embed`);
    if (!res.ok) return [];
    const data = await res.json();
    const list = Array.isArray(data) ? data : [];
    return list.slice(0, 6).map((s) => {
      const featured = s.featured_image_url || s.featuredImage || s.image;
      return {
        id: s.id ?? s.session_id,
        title: s.title ?? s.name ?? '',
        date: s.start_time
          ? new Date(s.start_time * 1000).toISOString()
          : s.date,
        featuredImage: featured
          ? { node: { sourceUrl: typeof featured === 'string' ? featured : featured?.url ?? featured?.source_url } }
          : null,
      };
    });
  } catch (err) {
    console.error('getSessionsForHome error:', err);
    return [];
  }
}

/**
 * Jobs for home carousel.
 */
export async function getJobsForHome() {
  try {
    const res = await fetchWithTimeout(
      `${baseURL}api/wp/v2/job?per_page=6&_embed`
    );
    if (!res.ok) return [];
    const data = await res.json();
    const list = Array.isArray(data) ? data : [];
    return list.map(mapWpItemToFeedNode);
  } catch (err) {
    console.error('getJobsForHome error:', err);
    return [];
  }
}

/**
 * Figma plugins (figplg) for home carousel.
 */
export async function getFigplgsForHome() {
  try {
    const res = await fetchWithTimeout(
      `${baseURL}api/wp/v2/figplg?per_page=6&_embed`
    );
    if (!res.ok) return [];
    const data = await res.json();
    const list = Array.isArray(data) ? data : [];
    return list.map(mapWpItemToFeedNode);
  } catch (err) {
    console.error('getFigplgsForHome error:', err);
    return [];
  }
}

/**
 * Figma templates (figtemp) for home carousel.
 */
export async function getFigtempsForHome() {
  try {
    const res = await fetchWithTimeout(
      `${baseURL}api/wp/v2/figtemp?per_page=6&_embed`
    );
    if (!res.ok) return [];
    const data = await res.json();
    const list = Array.isArray(data) ? data : [];
    return list.map(mapWpItemToFeedNode);
  } catch (err) {
    console.error('getFigtempsForHome error:', err);
    return [];
  }
}

/**
 * Speakers for home (experts/guests). Tries WP CPT "speaker" or returns empty.
 */
export async function getSpeakersForHome() {
  try {
    const res = await fetchWithTimeout(
      `${baseURL}api/wp/v2/speaker?per_page=12&_embed`
    );
    if (!res.ok) return [];
    const data = await res.json();
    const list = Array.isArray(data) ? data : [];
    return list
      .filter((item) => {
        const contribution = item?.acf?.contribution;
        return Array.isArray(contribution) && contribution.includes('sessions');
      })
      .map((item) => {
        const featured = item?._embedded?.['wp:featuredmedia']?.[0];
        return {
          id: item.id,
          title: item.title?.rendered ?? item.title ?? '',
          featuredImage: featured ? { node: { sourceUrl: featured.source_url } } : null,
        };
      });
  } catch (err) {
    console.error('getSpeakersForHome error:', err);
    return [];
  }
}
